/* 
When the date data is created in the frontend, we should store it in UTC milliseconds.
When the data is received from the backend, it is in UTC ISO datetime strings already.
*/

// Helper functions that deal with UTC milliseconds
export function convertGMT8ToUTC(dateTimeString: string) {
  // Parse the input string
  const [datePart, timePart] = dateTimeString.split('T')
  const [year, month, day] = datePart.split('-')
  const [hour, minute, second] = timePart.split(':')

  // Create a Date object in UTC, compensating for GMT+8
  const utcDate = new Date(
    Date.UTC(
      parseInt(year, 10),
      parseInt(month, 10) - 1, // Month is 0-indexed in JavaScript
      parseInt(day, 10),
      parseInt(hour, 10) - 8, // Subtract 8 hours for GMT+8
      parseInt(minute, 10),
      parseInt(second, 10)
    )
  )

  // Return the UTC milliseconds
  return utcDate.getTime()
}

export function convertUTCToGMT8(utcMilliseconds: number) {
  const date = new Date(utcMilliseconds)
  const options = {
    timeZone: 'Asia/Singapore', // Singapore is in GMT+8
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: false
  }

  // @ts-ignore
  const singaporeTime = date.toLocaleString('en-US', options)
  const [datepart, timepart] = singaporeTime.split(', ')
  const [month, day, year] = datepart.split('/')
  const timeSplit = timepart.split(':')
  const hour = timeSplit[0]
  const minute = timeSplit[1]

  return {
    year: parseInt(year, 10),
    month: parseInt(month, 10),
    day: parseInt(day, 10),
    hour: parseInt(hour, 10),
    minute: parseInt(minute, 10)
  }
}

export function formatDay(day: number, shortForm = false) {
  let dayStr = ''
  if (day === 0) dayStr = 'Sunday'
  else if (day === 1) dayStr = 'Monday'
  else if (day === 2) dayStr = 'Tuesday'
  else if (day === 3) dayStr = 'Wednesday'
  else if (day === 4) dayStr = 'Thursday'
  else if (day === 5) dayStr = 'Friday'
  else dayStr = 'Saturday'

  if (shortForm) return dayStr.slice(0, 3)
  else return dayStr
}

export function formatMonth(month: number, shortForm: boolean = false) {
  const m = month
  let mnth = ''

  if (m === 1) mnth = 'January'
  else if (m === 2) mnth = 'February'
  else if (m === 3) mnth = 'March'
  else if (m === 4) mnth = 'April'
  else if (m === 5) mnth = 'May'
  else if (m === 6) mnth = 'June'
  else if (m === 7) mnth = 'July'
  else if (m === 8) mnth = 'August'
  else if (m === 9) mnth = 'September'
  else if (m === 10) mnth = 'October'
  else if (m === 11) mnth = 'November'
  else mnth = 'December'

  return shortForm ? mnth.slice(0, 3) : mnth
}

export function formatDatePeriod(fromUTCMilliseconds: number, toUTCMilliseconds: number) {
  const startInLocalTime = convertUTCToGMT8(fromUTCMilliseconds)
  const startMonth = formatMonth(startInLocalTime.month)
  const endInLocalTime = convertUTCToGMT8(toUTCMilliseconds)
  const endMonth = formatMonth(endInLocalTime.month)
  const startDateString = `${startInLocalTime.day} ${startMonth} ${startInLocalTime.year}`
  const endDateString = `${endInLocalTime.day} ${endMonth} ${endInLocalTime.year}`

  // if same day
  if (startDateString === endDateString) return startDateString
  // if same month
  else if (startMonth === endMonth)
    return `${startInLocalTime.day} - ${endInLocalTime.day} ${startMonth} ${startInLocalTime.year}`
  // write both dates
  else return `${startDateString} - ${endDateString}`
}

export function formatHour(time: number) {
  if (time < 12) return `${time} am`
  else if (time === 12) return `${time} pm`
  else return `${time - 12} pm`
}

export function formatTimePeriod(fromUTCMilliseconds: number, toUTCMilliseconds: number) {
  const startInLocalTime = convertUTCToGMT8(fromUTCMilliseconds)
  const endInLocalTime = convertUTCToGMT8(toUTCMilliseconds)
  // start
  const startHours = startInLocalTime.hour
  const startHoursStr = startHours % 12 || 12
  const startPeriod = startHours >= 12 ? 'PM' : 'AM'
  const startMin = startInLocalTime.minute
  const startMinStr = startMin < 10 ? `0${startMin}` : startMin
  const startTime = `${startHoursStr}:${startMinStr} ${startPeriod}`
  // end
  const endHours = endInLocalTime.hour
  const endHoursStr = endHours % 12 || 12
  const endPeriod = endHours >= 12 ? 'PM' : 'AM'
  const endMin = endInLocalTime.minute
  const endMinStr = endMin < 10 ? `0${endMin}` : endMin
  const endTime = `${endHoursStr}:${endMinStr} ${endPeriod}`

  return `${startTime} - ${endTime}`
}

export function isDatePast(utcMilliseconds: number) {
  // given a utc datetime in milliseconds
  const utcNow = new Date().getTime()
  // const dateString = new Date(utcMilliseconds).toISOString()
  // const utcNowString = new Date(utcNow).toISOString()
  // console.log(dateString, utcNowString, utcMilliseconds < utcNow)
  return utcMilliseconds < utcNow
}

// Helper functions that deal with UTC ISO datetime strings
export function formatDatePeriodFromISOString(from: string, to: string | null) {
  // This function takes a date object in UTC that was generated from the backend
  const fromDate = new Date(from)
  const start = `${formatMonth(fromDate.getMonth() + 1)} ${fromDate.getFullYear()}`
  const end = to
    ? `${formatMonth(new Date(to).getMonth() + 1)} ${new Date(to).getFullYear()}`
    : 'Present'

  return `${start} - ${end}`
}

export function formatTimeDifferenceFromISOString(from: string, to: string) {
  // This function takes a date object in UTC that was generated from the backend
  const msPerDay = 1000 * 60 * 60 * 24
  const fromDate = new Date(from)
  const toDate = to ? new Date(to) : new Date()
  const utc1 = Date.UTC(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate())
  const utc2 = Date.UTC(toDate.getFullYear(), toDate.getMonth(), toDate.getDate())
  const days = Math.floor((utc2 - utc1) / msPerDay)
  // if more than 1 year
  if (days >= 365) {
    const years = Math.floor(days / 365)
    const leftoverDays = days - years * 365
    if (leftoverDays < 30) return `${years} year${years > 1 ? 's' : ''}`
    else {
      const months = Math.floor(leftoverDays / 30)
      return `${years} year${years > 1 ? 's' : ''} ${months} month${months > 1 ? 's' : ''}`
    }
  }
  // if less than 1 year but more than 1 month
  else if (days >= 30) {
    const months = Math.floor(days / 30)
    return `${months} month${months > 1 ? 's' : ''}`
  }
  // if less than 1 month
  else return `${days} day${days > 1 ? 's' : ''}`
}

export function formatFromISOString(dateInput: string, shortForm: boolean = false) {
  const date = new Date(dateInput)
  return `${date.getDate()} ${formatMonth(date.getMonth() + 1, shortForm)} ${date.getFullYear()}`
}

export function formatFromISOStringWithTime(dateInput: string, shortForm: boolean = false) {
  const date = new Date(dateInput)

  // min
  const min = date.getMinutes()
  const minStr = `${min < 10 ? 0 : ''}${min}`

  // hours
  const hours = date.getHours()
  const m = hours >= 12 ? 'pm' : 'am'
  const hours_m = hours > 12 ? hours - 12 : hours
  const hoursStr = `${hours_m < 10 ? '0' : ''}${hours_m}`
  const time = `${hoursStr}:${minStr} ${m}`

  return `${date.getDate()} ${formatMonth(date.getMonth() + 1, shortForm)} ${date.getFullYear()}, ${time}`
}

export function getTimeFromToday(dateInput: string) {
  const today = new Date()
  const createdOn = new Date(dateInput) // assume that date is saved in UTC already
  const minutes = Math.floor((today.getTime() - createdOn.getTime()) / (60 * 1000))

  if (minutes <= 1) return `1min ago`
  if (minutes < 60) return `${minutes}min ago`

  // Hours
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`

  // Days
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`

  // Weeks
  const weeks = Math.floor(days / 7)
  if (weeks < 4) return `${weeks}w ago`

  // Months
  const months = Math.floor(weeks / 4)
  if (months < 12) return `${months}mo ago`

  // Years
  const years = Math.floor(months / 12)
  return `${years}y ago`
}

export function getTimeInFuture(dateInput: string, isDetailed: boolean = false) {
  const today = new Date()
  const future = new Date(dateInput) // assume that date is saved in UTC already
  const minutes = Math.floor((future.getTime() - today.getTime()) / (60 * 1000))

  if (minutes <= 1) return `less than 1 minute`
  if (minutes < 60) return `${minutes} minutes`

  // Hours
  const hours = Math.floor(minutes / 60)
  const hourStr = `${hours} hour${hours > 1 ? 's' : ''}`
  if (hours < 24) {
    if (isDetailed) {
      const remainder = minutes % 60
      const remainderStr = `${remainder} minute${remainder > 1 ? 's' : ''}`
      return `${hourStr} ${remainderStr}`
    } else return hourStr
  }

  // Days
  const days = Math.floor(hours / 24)
  const daysStr = `${days} day${days > 1 ? 's' : ''}`
  if (days < 30) {
    if (isDetailed && days < 14) {
      const remainder = hours % 24
      const remainderStr = `${remainder} hour${remainder > 1 ? 's' : ''}`
      return `${daysStr} ${remainderStr}`
    } else return daysStr
  }

  // Months
  const months = Math.floor(days / 30)
  if (months < 12) return `${months} month${months > 1 ? 's' : ''}`

  // Years
  const years = Math.floor(months / 12)
  return `${years} year${years > 1 ? 's' : ''}`
}

export const formatHoursMinutes = (isoString: string) => {
  const date = new Date(isoString)
  const h = date.getHours()
  const hoursStr = h % 12 || 12
  const period = date.getHours() >= 12 ? 'PM' : 'AM'
  const m = date.getMinutes()
  const minStr = m < 10 ? `0${m}` : m

  return `${hoursStr}:${minStr} ${period}`
}
